/**
 * Registry of editorial block schemas (Phase 2).
 *
 * Every block_key present in the seed corresponds to an entry here.
 * The `PageEditor` looks up the schema by `${page_key}.${block_key}` and
 * renders the appropriate editor + item schema.
 *
 * Field types recognised by <FixedEditor>:
 *   - text        → single-line Input
 *   - longtext    → Textarea
 *   - url         → Input url
 *   - image       → Upload field (Supabase Storage)
 *   - boolean     → Switch
 *   - number      → Input type=number
 *   - chips       → Chip input (array of strings)
 *   - highlight   → Chip input for highlight_terms
 *   - markdown    → Textarea with split preview
 *   - template    → Input with hint of allowed placeholders
 */

// Field definitions shared across many hero blocks.
const HERO_FIELDS = [
  { key: "pretitle", label: "Etichetta soprattitolo", type: "text", max: 60 },
  { key: "headline", label: "Titolo principale", type: "longtext", rows: 2, max: 140 },
  { key: "highlight_terms", label: "Parole da evidenziare in verde", type: "highlight",
    help: "Aggiungi porzioni esatte del titolo per applicare lo stile evidenziato." },
  { key: "description", label: "Descrizione", type: "longtext", rows: 4, max: 400 },
  { key: "image_url", label: "Immagine di sfondo", type: "image", folder: "content/hero" },
  { key: "image_alt", label: "Testo alternativo (accessibilità)", type: "text", max: 140 },
];

const HERO_HOME_FIELDS = [
  ...HERO_FIELDS,
  { key: "search_placeholder", label: "Placeholder ricerca", type: "text" },
  { key: "category_all_label", label: "Etichetta \"Tutte le categorie\"", type: "text" },
  { key: "cta_label", label: "Etichetta pulsante \"Esplora\"", type: "text" },
  { key: "popular_prefix", label: "Prefisso ricerche popolari", type: "text" },
  { key: "popular_chips", label: "Ricerche suggerite (max 6)", type: "chips", max_items: 6 },
];

const CTA_FIELDS = [
  { key: "pretitle", label: "Soprattitolo", type: "text" },
  { key: "headline", label: "Titolo principale", type: "longtext", rows: 2, max: 140 },
  { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
  { key: "description", label: "Descrizione", type: "longtext", rows: 3 },
  { key: "primary_cta_label", label: "Testo pulsante primario", type: "text" },
  { key: "primary_cta_url", label: "URL pulsante primario", type: "url" },
  { key: "secondary_cta_label", label: "Testo pulsante secondario (opzionale)", type: "text" },
  { key: "secondary_cta_url", label: "URL pulsante secondario (opzionale)", type: "url" },
];

export const BLOCK_SCHEMAS = {
  // -------------------- HOME --------------------
  "home.hero": { label: "Hero principale", type: "fixed", fields: HERO_HOME_FIELDS },
  "home.category_grid": {
    label: "Titolo sezione categorie", type: "fixed",
    fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "longtext", rows: 2 },
      { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
      { key: "description", label: "Descrizione", type: "longtext", rows: 3 },
    ],
  },
  "home.spotlight": {
    label: "Sezione \"Attività del centro\"", type: "fixed",
    fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "longtext", rows: 2 },
      { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
      { key: "subtitle", label: "Sottotitolo", type: "text" },
      { key: "cta_label", label: "Etichetta pulsante", type: "text" },
      { key: "cta_url", label: "URL pulsante", type: "url" },
    ],
  },
  "home.stats": {
    label: "Statistiche in numeri", type: "list",
    header_fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "longtext", rows: 2 },
      { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
    ],
    item_schema: [
      { key: "value", label: "Valore", type: "number" },
      { key: "suffix", label: "Suffisso (es. +, K)", type: "text" },
      { key: "label", label: "Etichetta", type: "text" },
    ],
    item_label: (item) => `${item.value ?? "—"}${item.suffix || ""} · ${item.label || "…"}`,
    max_items: 4,
  },
  "home.join_banner": {
    label: "Banner \"Diventa socio\"", type: "fixed",
    fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "text" },
      { key: "subheadline", label: "Sottotitolo", type: "text" },
      { key: "description", label: "Descrizione", type: "longtext", rows: 3 },
      { key: "cta_label", label: "Testo pulsante", type: "text" },
      { key: "cta_url", label: "URL pulsante", type: "url" },
      { key: "micro_text", label: "Testo piccolo sotto il pulsante", type: "text" },
    ],
  },
  "home.events_section": {
    label: "Sezione \"Eventi\" home", type: "fixed",
    fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "longtext", rows: 2 },
      { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
      { key: "cta_label", label: "Etichetta pulsante \"Tutti gli eventi\"", type: "text" },
      { key: "cta_url", label: "URL pulsante", type: "url" },
    ],
  },
  "home.testimonials": {
    label: "Recensioni / Testimonianze", type: "list",
    header_fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "text" },
      { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
      { key: "description", label: "Descrizione", type: "longtext", rows: 3 },
    ],
    item_schema: [
      { key: "name", label: "Nome", type: "text" },
      { key: "role", label: "Ruolo / attività", type: "text" },
      { key: "quote", label: "Testimonianza", type: "longtext", rows: 3 },
      { key: "avatar_url", label: "Foto (opzionale)", type: "image", folder: "content/testimonials" },
      { key: "visible", label: "Visibile", type: "boolean" },
    ],
    item_label: (item) => `${item.name || "…"} — ${item.role || ""}`,
  },
  "home.faq_home": {
    label: "FAQ home page", type: "list",
    header_fields: [
      { key: "pretitle", label: "Soprattitolo", type: "text" },
      { key: "headline", label: "Titolo", type: "text" },
      { key: "highlight_terms", label: "Parole evidenziate", type: "highlight" },
      { key: "description", label: "Descrizione", type: "longtext", rows: 3 },
    ],
    item_schema: [
      { key: "q", label: "Domanda", type: "text" },
      { key: "a", label: "Risposta", type: "longtext", rows: 4 },
      { key: "visible", label: "Visibile", type: "boolean" },
    ],
    item_label: (item) => item.q || "Domanda senza testo",
  },
  "home.partners": {
    label: "Partner / Sostenitori", type: "list",
    header_fields: [
      { key: "label", label: "Etichetta \"Con il supporto di\"", type: "text" },
    ],
    item_schema: [
      { key: "name", label: "Nome partner", type: "text" },
      { key: "logo_url", label: "Logo (opzionale)", type: "image", folder: "content/partners" },
      { key: "url", label: "Link sito (opzionale)", type: "url" },
      { key: "visible", label: "Visibile", type: "boolean" },
    ],
    item_label: (item) => item.name || "…",
  },
  "home.cta_final": { label: "CTA finale home", type: "fixed", fields: CTA_FIELDS },

  // -------------------- ABOUT --------------------
  "about.hero": {
    label: "Hero \"Chi siamo\"", type: "fixed",
    fields: [
      ...HERO_FIELDS,
      { key: "image_caption", label: "Didascalia foto", type: "text" },
    ],
  },
  "about.accordion_ccn": {
    label: "Accordion \"Cos'è un CCN\"", type: "markdown",
    fields: [
      { key: "title", label: "Titolo accordion", type: "text" },
      { key: "paragraphs_ccn", label: "Paragrafi CCN", type: "paragraphs" },
      { key: "bullets", label: "Elenco puntato", type: "chips" },
      { key: "sub_heading", label: "Sotto-titolo (opzionale)", type: "text" },
      { key: "paragraphs_sanluri", label: "Paragrafi CCN Sanluri", type: "paragraphs" },
    ],
  },
  "about.accordion_statuto": {
    label: "Accordion \"Statuto\"", type: "fixed",
    fields: [
      { key: "title", label: "Titolo accordion", type: "text" },
      { key: "paragraphs", label: "Paragrafi", type: "paragraphs" },
      { key: "cta_label", label: "Etichetta bottone download", type: "text" },
      { key: "empty_state_text", label: "Testo quando lo statuto non è disponibile", type: "text" },
    ],
  },
  "about.accordion_logo": {
    label: "Accordion \"Il nostro logo\"", type: "fixed",
    fields: [
      { key: "title", label: "Titolo accordion", type: "text" },
      { key: "description", label: "Descrizione", type: "longtext", rows: 4 },
      { key: "logo_image_url", label: "Immagine logo", type: "image", folder: "content/logo" },
      { key: "logo_image_alt", label: "Testo alternativo logo", type: "text" },
    ],
  },

  // -------------------- CONTACT --------------------
  "contact.hero": { label: "Hero contatti", type: "fixed", fields: HERO_FIELDS.slice(0, 4) },
  "contact.info_card": {
    label: "Card informazioni contatti", type: "fixed",
    fields: [
      { key: "title", label: "Titolo card", type: "text" },
      { key: "address_label", label: "Etichetta \"Indirizzo\"", type: "text" },
      { key: "phone_label", label: "Etichetta \"Telefono\"", type: "text" },
      { key: "email_label", label: "Etichetta \"Email\"", type: "text" },
      { key: "hours_title", label: "Titolo orari", type: "text" },
      { key: "hours_days", label: "Giorni", type: "text" },
      { key: "hours_range", label: "Fascia oraria", type: "text" },
    ],
  },
  "contact.form_notice": {
    label: "Alert form + testi form", type: "fixed",
    fields: [
      { key: "active", label: "Alert attivo", type: "boolean" },
      { key: "alert_title", label: "Titolo alert", type: "text" },
      { key: "alert_text", label: "Testo alert", type: "longtext", rows: 3 },
      { key: "direct_title", label: "Titolo sezione \"Contattaci direttamente\"", type: "text" },
      { key: "direct_description", label: "Descrizione sezione", type: "longtext", rows: 2 },
      { key: "form_disabled_label", label: "Etichetta form disabilitato", type: "text" },
      { key: "placeholder_name", label: "Placeholder Nome", type: "text" },
      { key: "placeholder_email", label: "Placeholder Email", type: "text" },
      { key: "placeholder_message", label: "Placeholder Messaggio", type: "text" },
    ],
  },
  "contact.map": {
    label: "Mappa contatti", type: "fixed",
    fields: [
      { key: "embed_url", label: "URL embed OpenStreetMap", type: "url" },
      { key: "title", label: "Titolo iframe (accessibilità)", type: "text" },
    ],
  },

  // -------------------- JOIN --------------------
  "join.hero": {
    label: "Hero \"Diventa socio\"", type: "fixed",
    fields: [
      ...HERO_FIELDS,
      { key: "headline_highlight_url", label: "Porzione titolo linkata (opzionale)", type: "text" },
    ],
  },
  "join.form_labels": {
    label: "Etichette form associazione", type: "list",
    header_fields: [
      { key: "submit_label", label: "Etichetta pulsante invio", type: "text" },
      { key: "submitting_label", label: "Etichetta durante invio", type: "text" },
      { key: "info_footer", label: "Testo sotto il form", type: "longtext", rows: 2 },
    ],
    items_field: "step_titles",
    item_schema: [
      { key: "n", label: "Numero step", type: "number" },
      { key: "title", label: "Titolo step", type: "text" },
      { key: "subtitle", label: "Sottotitolo step", type: "text" },
    ],
    item_label: (item) => `Step ${item.n} — ${item.title}`,
  },
  "join.consent": {
    label: "Testi consenso (HTML)", type: "markdown",
    fields: [
      { key: "privacy_html", label: "Testo consenso privacy (HTML)", type: "longtext", rows: 4 },
      { key: "terms_html", label: "Testo consenso termini (HTML)", type: "longtext", rows: 4 },
    ],
  },
  "join.success": {
    label: "Schermata di successo", type: "fixed",
    fields: [
      { key: "title", label: "Titolo", type: "text" },
      { key: "message_template", label: "Messaggio (usa {first_name} e {business_name})", type: "template",
        placeholders: ["{first_name}", "{business_name}"] },
      { key: "code_label", label: "Etichetta \"Codice richiesta:\"", type: "text" },
      { key: "primary_cta_label", label: "Pulsante primario", type: "text" },
      { key: "primary_cta_url", label: "URL primario", type: "url" },
      { key: "secondary_cta_label", label: "Pulsante secondario", type: "text" },
      { key: "secondary_cta_url", label: "URL secondario", type: "url" },
    ],
  },

  // -------------------- EVENTS list --------------------
  "events.hero": { label: "Hero pagina Eventi", type: "fixed", fields: HERO_FIELDS.slice(0, 4) },
  "events.filters": {
    label: "Etichette filtri eventi", type: "list",
    header_fields: [
      { key: "view_grid_label", label: "Etichetta vista griglia", type: "text" },
      { key: "view_list_label", label: "Etichetta vista elenco", type: "text" },
    ],
    items_field: "date_filters",
    item_schema: [
      { key: "value", label: "Chiave tecnica", type: "text" },
      { key: "label", label: "Etichetta", type: "text" },
    ],
    item_label: (item) => `${item.value} — ${item.label}`,
  },
  "events.empty_state": {
    label: "Messaggi vuoti", type: "fixed",
    fields: [
      { key: "loading_label", label: "Caricamento…", type: "text" },
      { key: "title", label: "Titolo empty state", type: "text" },
      { key: "subtitle", label: "Sottotitolo empty state", type: "longtext", rows: 2 },
    ],
  },

  // -------------------- BUSINESSES list --------------------
  "businesses.hero": { label: "Hero pagina Attività", type: "fixed", fields: HERO_FIELDS.slice(0, 3) },
  "businesses.filters": {
    label: "Filtri attività", type: "list",
    header_fields: [
      { key: "search_placeholder", label: "Placeholder ricerca", type: "text" },
      { key: "all_categories_label", label: "Etichetta \"Tutte\"", type: "text" },
    ],
    items_field: "sort_options",
    item_schema: [
      { key: "value", label: "Chiave tecnica", type: "text" },
      { key: "label", label: "Etichetta", type: "text" },
    ],
    item_label: (item) => `${item.value} — ${item.label}`,
  },
  "businesses.labels": {
    label: "Etichette generiche", type: "fixed",
    fields: [
      { key: "count_template", label: "Template contatore (usa {n})", type: "template", placeholders: ["{n}"] },
      { key: "location_label", label: "Etichetta località", type: "text" },
    ],
  },
  "businesses.empty_states": {
    label: "Messaggi vuoti e errori", type: "fixed",
    fields: [
      { key: "no_results_title", label: "Titolo nessun risultato", type: "text" },
      { key: "no_results_subtitle", label: "Sottotitolo nessun risultato", type: "longtext", rows: 2 },
      { key: "error_title", label: "Titolo errore config", type: "text" },
      { key: "error_hint", label: "Testo suggerimento errore", type: "longtext", rows: 2 },
    ],
  },

  // -------------------- NAV --------------------
  "nav.menu_items": {
    label: "Voci del menu principale", type: "list",
    header_fields: [],
    item_schema: [
      { key: "label", label: "Etichetta", type: "text" },
      { key: "url", label: "URL", type: "text" },
      { key: "target", label: "Target", type: "text", help: "_self o _blank" },
      { key: "is_mega", label: "Apre mega menu", type: "boolean" },
    ],
    item_label: (item) => `${item.label} — ${item.url}`,
  },
  "nav.mega_menu": {
    label: "Mega menu \"Attività associate\"", type: "fixed",
    fields: [
      { key: "title", label: "Titolo", type: "text" },
      { key: "subtitle", label: "Sottotitolo", type: "text" },
      { key: "cta_label", label: "CTA principale", type: "text" },
      { key: "cta_url", label: "URL CTA principale", type: "url" },
      { key: "promo_html", label: "Testo promo (HTML)", type: "longtext", rows: 2 },
      { key: "promo_cta_label", label: "Etichetta promo", type: "text" },
      { key: "promo_cta_url", label: "URL promo", type: "url" },
    ],
  },
  "nav.header_ctas": {
    label: "CTA header", type: "fixed",
    fields: [
      { key: "join_label", label: "Etichetta \"Associati\"", type: "text" },
      { key: "join_url", label: "URL \"Associati\"", type: "url" },
      { key: "search_url", label: "URL ricerca", type: "url" },
      { key: "search_visible", label: "Icona ricerca visibile", type: "boolean" },
    ],
  },

  // -------------------- FOOTER --------------------
  "footer.intro": {
    label: "Intro footer", type: "fixed",
    fields: [
      { key: "brand_subtitle", label: "Sottotitolo brand", type: "text" },
      { key: "headline", label: "Titolo", type: "longtext", rows: 2 },
      { key: "description", label: "Descrizione", type: "longtext", rows: 3 },
    ],
  },
  "footer.column_explore": {
    label: "Colonna \"Esplora\"", type: "list",
    header_fields: [
      { key: "title", label: "Titolo colonna", type: "text" },
    ],
    item_schema: [
      { key: "label", label: "Etichetta", type: "text" },
      { key: "url", label: "URL", type: "text" },
    ],
    item_label: (item) => `${item.label}`,
  },
  "footer.column_categories_title": {
    label: "Titolo colonna Categorie", type: "fixed",
    fields: [{ key: "title", label: "Titolo colonna", type: "text" }],
  },
  "footer.column_contacts_title": {
    label: "Titolo colonna Contatti", type: "fixed",
    fields: [{ key: "title", label: "Titolo colonna", type: "text" }],
  },
  "footer.legal_links": {
    label: "Link legali footer", type: "fixed",
    fields: [
      { key: "privacy_label", label: "Etichetta Privacy Policy", type: "text" },
      { key: "privacy_url", label: "URL Privacy Policy", type: "url" },
      { key: "cookie_label", label: "Etichetta Cookie Policy", type: "text" },
      { key: "cookie_url", label: "URL Cookie Policy", type: "url" },
    ],
  },
  "footer.copyright": {
    label: "Copyright", type: "fixed",
    fields: [
      { key: "template", label: "Template (usa {year} e {name})", type: "template", placeholders: ["{year}", "{name}"] },
      { key: "name", label: "Nome organizzazione", type: "text" },
    ],
  },
  "footer.credit": {
    label: "Credit sviluppatore", type: "fixed",
    fields: [
      { key: "prefix", label: "Prefisso", type: "text" },
      { key: "label", label: "Etichetta cliccabile", type: "text" },
      { key: "url", label: "URL", type: "url" },
      { key: "visible", label: "Visibile", type: "boolean" },
    ],
  },

  // -------------------- DETAIL LABELS --------------------
  "business_detail.labels": {
    label: "Etichette scheda attività", type: "fixed",
    fields: [
      { key: "gallery_pretitle", label: "Soprattitolo galleria", type: "text" },
      { key: "gallery_title", label: "Titolo galleria", type: "text" },
      { key: "gallery_browse_all", label: "\"Sfoglia tutte\"", type: "text" },
      { key: "about_pretitle", label: "Soprattitolo \"Chi siamo\"", type: "text" },
      { key: "about_title", label: "Titolo \"La nostra storia\"", type: "text" },
      { key: "services_pretitle", label: "Soprattitolo servizi", type: "text" },
      { key: "services_title", label: "Titolo servizi", type: "text" },
      { key: "map_pretitle", label: "Soprattitolo mappa", type: "text" },
      { key: "map_title", label: "Titolo mappa", type: "text" },
      { key: "contacts_title", label: "Titolo contatti", type: "text" },
      { key: "social_title", label: "Titolo social", type: "text" },
      { key: "hours_title", label: "Titolo orari", type: "text" },
      { key: "related_pretitle", label: "Soprattitolo attività correlate", type: "text" },
      { key: "related_title_template", label: "Template titolo correlate (usa {category})", type: "template", placeholders: ["{category}"] },
    ],
  },
  "event_detail.labels": {
    label: "Etichette scheda evento", type: "fixed",
    fields: [
      { key: "section_description", label: "\"Descrizione\"", type: "text" },
      { key: "section_timeline", label: "\"Programma\"", type: "text" },
      { key: "section_gallery", label: "\"Gallery\"", type: "text" },
      { key: "section_details", label: "\"Dettagli\"", type: "text" },
      { key: "section_organizers", label: "\"Contatti organizzatore\"", type: "text" },
      { key: "section_amenities", label: "\"Informazioni utili\"", type: "text" },
      { key: "section_downloads", label: "\"Download\"", type: "text" },
      { key: "section_share", label: "\"Condividi evento\"", type: "text" },
      { key: "cta_calendar", label: "CTA calendario", type: "text" },
      { key: "cta_maps", label: "CTA Google Maps", type: "text" },
      { key: "nearby_title", label: "Titolo \"Nelle vicinanze\"", type: "text" },
      { key: "related_title", label: "Titolo \"Potrebbero interessarti\"", type: "text" },
      { key: "discover_title", label: "Titolo \"Scopri Sanluri\"", type: "text" },
    ],
  },
};

/** Human labels for pages (fallback = page_key). */
export const PAGE_LABELS = {
  home: "Home",
  about: "Chi siamo",
  contact: "Contatti",
  join: "Diventa socio",
  events: "Eventi",
  businesses: "Attività",
  nav: "Navbar",
  footer: "Footer",
  business_detail: "Etichette scheda attività",
  event_detail: "Etichette scheda evento",
};

/** Tabs displayed in the Site Web sub-nav (in this order). */
export const SITE_TABS = [
  { key: "dashboard", label: "Dashboard", icon: "LayoutDashboard" },
  { key: "home", label: "Home", icon: "Home" },
  { key: "about", label: "Chi siamo", icon: "Info" },
  { key: "businesses", label: "Attività", icon: "Store" },
  { key: "events", label: "Eventi", icon: "Calendar" },
  { key: "contact", label: "Contatti", icon: "Mail" },
  { key: "join", label: "Diventa socio", icon: "Handshake" },
  { key: "footer", label: "Footer", icon: "AlignEndVertical" },
  { key: "nav", label: "Navbar", icon: "Menu" },
  { key: "business_detail", label: "Etich. attività", icon: "Store" },
  { key: "event_detail", label: "Etich. evento", icon: "Calendar" },
  { key: "ui-strings", label: "Testi interfaccia", icon: "Type" },
  { key: "seo", label: "SEO", icon: "Search" },
];

export function getSchema(pageKey, blockKey) {
  return BLOCK_SCHEMAS[`${pageKey}.${blockKey}`] || null;
}
